import { useState } from 'react';
import { Ban } from 'lucide-react';
import { patchTask } from '../../lib/requirement-card';
import { type MemberDto } from '../../lib/work-types';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import BlockedReasonDialog, { blockedByIdsOf, blockedByNames, type BlockedSubmit } from './BlockedReasonDialog';
import { useOverlays } from './overlay-context';

type Blockable = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  blocked_reason?: string | null;
  blocked_by_id?: string | null;
  blocked_by_ids?: string[];
};

/** Row-sized blocked control for requirement cards and subtask lists. */
export default function BlockedToggle({
  item,
  members,
  readOnly,
  onChanged,
  labelled,
  className,
}: {
  item: Blockable;
  members?: MemberDto[];
  readOnly?: boolean;
  onChanged: () => void | Promise<void>;
  /** Show "Mark blocked" as text, for rows with room for it. */
  labelled?: boolean;
  className?: string;
}) {
  const { toast } = useOverlays();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const reason = item.blocked_reason ?? null;
  const blockerIds = blockedByIdsOf(item);
  const blockers = blockedByNames(blockerIds, members);

  async function save(fields: Record<string, unknown>, failure: string) {
    try {
      await patchTask(item.project_id, item.id, fields);
      await onChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : failure);
      throw err;
    }
  }

  const dialog = (
    <BlockedReasonDialog
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      workItemKey={item.key}
      projectId={item.project_id}
      members={members}
      initialReason={reason}
      initialBlockedByIds={blockerIds}
      onSubmit={(next: BlockedSubmit) =>
        save({ blocked_reason: next.reason, blocked_by_ids: next.blockedByIds }, 'Could not mark it blocked')
      }
    />
  );

  if (!reason) {
    if (readOnly) return null;
    if (labelled) {
      return (
        <>
          <Button type="button" size="sm" variant="outline" className={cn('h-8', className)} onClick={() => setDialogOpen(true)}>
            <Ban className="h-3.5 w-3.5" aria-hidden />
            Mark blocked
          </Button>
          {dialog}
        </>
      );
    }
    return (
      <>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('h-7 w-7 text-muted-foreground hover:text-red-600', className)}
          aria-label={`Mark ${item.title} blocked`}
          title="Mark blocked"
          onClick={() => setDialogOpen(true)}
        >
          <Ban className="h-3.5 w-3.5" aria-hidden />
        </Button>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${item.title} is blocked`}
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 hover:bg-red-200 dark:bg-red-950 dark:text-red-200',
              className,
            )}
          >
            <Ban className="h-3 w-3" aria-hidden />
            Blocked
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 space-y-2 p-3 text-sm">
          <p className="font-medium">Blocked</p>
          <p className="whitespace-pre-wrap break-words text-muted-foreground">{reason}</p>
          {blockers ? (
            <p>
              <span className="font-medium">Blocked by:</span> {blockers}
            </p>
          ) : null}
          {readOnly ? null : (
            <div className="flex justify-end gap-1.5 pt-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setPopoverOpen(false);
                  setDialogOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setPopoverOpen(false);
                  void save({ blocked_reason: null }, 'Could not unblock it').catch(() => undefined);
                }}
              >
                Unblock
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {dialog}
    </>
  );
}
